/**
 * Audio engine for the verse recording pipeline.
 *
 * Recording strategy:
 *  - The raw microphone is captured on its own track (no beat baked in).
 *  - After the take, the vocal is mixed with the pristine beat file in an
 *    OfflineAudioContext (high-pass + compression + leveling on the vocal,
 *    latency alignment, peak normalization).
 *  - The final mix is encoded to MP3 (lamejs, lazily loaded) with WAV as a
 *    backup encoder.
 */

/** Extra capture latency we cannot measure (mic ADC + capture pipeline). */
const ASSUMED_INPUT_LATENCY_SEC = 0.03;

/** Mix defaults chosen so vocals sit on top of the beat. */
export const DEFAULT_VOCAL_GAIN = 1.0;
export const DEFAULT_BEAT_GAIN = 0.85;

export interface RecorderMimeChoice {
  mimeType: string;
  extension: string;
}

/** Pick the best supported container/codec for MediaRecorder. */
export function pickRecorderMime(): RecorderMimeChoice {
  if (typeof window === 'undefined' || !window.MediaRecorder) {
    return { mimeType: '', extension: 'webm' };
  }
  const candidates: RecorderMimeChoice[] = [
    { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
    { mimeType: 'audio/webm', extension: 'webm' },
    { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg' },
    { mimeType: 'audio/ogg', extension: 'ogg' },
  ];
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate.mimeType)) {
      return candidate;
    }
  }
  return { mimeType: '', extension: 'webm' };
}

/**
 * Microphone constraints tuned for music capture.
 *
 * With headphones the browser's voice-call processing is disabled entirely:
 * echo cancellation ducks vocals against the beat, noise suppression chops
 * sustained notes, and auto gain causes volume pumping.
 *
 * Without headphones the beat physically bleeds from the speakers into the
 * mic, so echo cancellation / noise suppression stay on to fight it. Auto
 * gain stays off in both profiles; the offline mix levels the vocal instead.
 */
export function getMicConstraints(usingHeadphones: boolean): MediaStreamConstraints {
  if (usingHeadphones) {
    return {
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
      },
    };
  }
  return {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: false,
    },
  };
}

let decodeContext: AudioContext | null = null;

function getDecodeContext(): AudioContext {
  if (!decodeContext || decodeContext.state === 'closed') {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    decodeContext = new Ctor();
  }
  return decodeContext;
}

export async function decodeBlobToAudioBuffer(blob: Blob): Promise<AudioBuffer> {
  const arrayBuffer = await blob.arrayBuffer();
  return getDecodeContext().decodeAudioData(arrayBuffer);
}

export async function fetchAndDecodeAudio(url: string): Promise<AudioBuffer> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch beat audio (${response.status})`);
  }
  const arrayBuffer = await response.arrayBuffer();
  return getDecodeContext().decodeAudioData(arrayBuffer);
}

/**
 * Estimate how much later the captured vocal is, relative to the beat
 * timeline, purely from playback/capture latency (excludes the measured
 * recorder-to-beat start gap, which the recorder adds separately).
 */
export function estimateLatencySkewSec(ctx: AudioContext): number {
  const base = typeof ctx.baseLatency === 'number' ? ctx.baseLatency : 0;
  const output = typeof (ctx as any).outputLatency === 'number' ? (ctx as any).outputLatency : 0;
  return base + output + ASSUMED_INPUT_LATENCY_SEC;
}

export interface VerseMixParams {
  vocalBuffer: AudioBuffer;
  beatBuffer: AudioBuffer;
  /** User-facing vocal volume multiplier (applied on top of auto leveling). */
  vocalGain: number;
  /** Beat volume multiplier in the final mix. */
  beatGain: number;
  /**
   * Seconds of the vocal capture to skip so vocals align with the beat
   * (start gap + playback/capture latency + user nudge). Negative values
   * delay the vocal instead.
   */
  vocalSkipSec: number;
}

function pickRenderSampleRate(): number {
  const deviceRate = getDecodeContext().sampleRate;
  // Keep to rates every decoder/encoder (incl. MP3) handles natively.
  return deviceRate === 44100 || deviceRate === 48000 ? deviceRate : 48000;
}

function bufferPeak(buffer: AudioBuffer): number {
  let peak = 0;
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < data.length; i++) {
      const abs = Math.abs(data[i]);
      if (abs > peak) peak = abs;
    }
  }
  return peak;
}

/**
 * Mix the vocal take with the beat in an offline (faster-than-realtime)
 * render. Vocal chain: high-pass (rumble removal) -> leveling gain ->
 * compressor (evens out delivery). The result is peak-normalized.
 */
export async function renderVerseMix(params: VerseMixParams): Promise<AudioBuffer> {
  const { vocalBuffer, beatBuffer, vocalGain, beatGain, vocalSkipSec } = params;

  const sampleRate = pickRenderSampleRate();
  const skip = Math.max(0, vocalSkipSec);
  const preDelay = Math.max(0, -vocalSkipSec);
  const durationSec = Math.max(0.5, vocalBuffer.duration - skip + preDelay);

  const OfflineCtor: typeof OfflineAudioContext =
    window.OfflineAudioContext || (window as any).webkitOfflineAudioContext;
  const ctx = new OfflineCtor(2, Math.ceil(durationSec * sampleRate), sampleRate);

  // --- Beat: loops for the length of the take, exactly like during recording.
  const beatSource = ctx.createBufferSource();
  beatSource.buffer = beatBuffer;
  beatSource.loop = true;
  const beatGainNode = ctx.createGain();
  beatGainNode.gain.value = beatGain;
  beatSource.connect(beatGainNode);
  beatGainNode.connect(ctx.destination);
  beatSource.start(0);
  beatSource.stop(durationSec);

  // --- Vocal chain.
  const vocalSource = ctx.createBufferSource();
  vocalSource.buffer = vocalBuffer;

  const highPass = ctx.createBiquadFilter();
  highPass.type = 'highpass';
  highPass.frequency.value = 80;
  highPass.Q.value = 0.707;

  // Auto leveling: quiet built-in mics produce very low signal with auto gain
  // control disabled, so bring the vocal peak near full scale first (capped
  // so a silent take is not boosted into pure noise).
  const vocalPeak = bufferPeak(vocalBuffer);
  const autoLevelGain = vocalPeak > 0.001 ? Math.min(6, 0.7 / vocalPeak) : 1;

  const vocalGainNode = ctx.createGain();
  vocalGainNode.gain.value = autoLevelGain * vocalGain;

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -18;
  compressor.knee.value = 12;
  compressor.ratio.value = 3;
  compressor.attack.value = 0.01;
  compressor.release.value = 0.2;

  vocalSource.connect(highPass);
  highPass.connect(vocalGainNode);
  vocalGainNode.connect(compressor);
  compressor.connect(ctx.destination);
  vocalSource.start(preDelay, skip);

  const rendered = await ctx.startRendering();

  // Peak-normalize to ~-0.5 dBFS so the sum never clips and quiet mixes get
  // a consistent loudness.
  const peak = bufferPeak(rendered);
  const target = 0.94;
  if (peak > 0) {
    const scale = target / peak;
    // Only touch samples when it matters (avoid needless work for ~unity).
    if (scale < 0.999 || scale > 1.2) {
      const applied = Math.min(scale, 4); // never boost more than +12 dB
      for (let ch = 0; ch < rendered.numberOfChannels; ch++) {
        const data = rendered.getChannelData(ch);
        for (let i = 0; i < data.length; i++) {
          data[i] *= applied;
        }
      }
    }
  }

  return rendered;
}

/** Encode an AudioBuffer as 16-bit PCM WAV (fast, dependency-free backup). */
export function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numChannels = Math.min(2, buffer.numberOfChannels);
  const sampleRate = buffer.sampleRate;
  const numFrames = buffer.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = numFrames * blockAlign;

  const arrayBuffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let ch = 0; ch < numChannels; ch++) {
    channels.push(buffer.getChannelData(ch));
  }

  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

function floatTo16(data: Float32Array): Int16Array {
  const out = new Int16Array(data.length);
  for (let i = 0; i < data.length; i++) {
    const sample = Math.max(-1, Math.min(1, data[i]));
    out[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return out;
}

/**
 * Encode an AudioBuffer to MP3. The encoder is imported lazily so it never
 * weighs down the initial bundle; encoding yields to the event loop
 * periodically to keep the UI responsive.
 */
export async function audioBufferToMp3Blob(buffer: AudioBuffer, kbps = 192): Promise<Blob> {
  const lame = await import('@breezystack/lamejs');

  const numChannels = Math.min(2, buffer.numberOfChannels);
  const sampleRate = buffer.sampleRate;
  const left = floatTo16(buffer.getChannelData(0));
  const right = numChannels > 1 ? floatTo16(buffer.getChannelData(1)) : null;

  const encoder = new lame.Mp3Encoder(numChannels, sampleRate, kbps);
  const blockSize = 1152;
  const chunks: BlobPart[] = [];

  for (let i = 0; i < left.length; i += blockSize) {
    const leftChunk = left.subarray(i, i + blockSize);
    const encoded = right
      ? encoder.encodeBuffer(leftChunk, right.subarray(i, i + blockSize))
      : encoder.encodeBuffer(leftChunk);
    if (encoded.length > 0) {
      chunks.push(new Uint8Array(encoded));
    }
    // Yield roughly every ~5 seconds of audio so long verses don't freeze
    // the page while encoding.
    if (i % (blockSize * 200) === 0 && i > 0) {
      await new Promise<void>(resolve => setTimeout(resolve, 0));
    }
  }

  const tail = encoder.flush();
  if (tail.length > 0) {
    chunks.push(new Uint8Array(tail));
  }

  return new Blob(chunks, { type: 'audio/mpeg' });
}
